import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteField,
  doc,
  documentId,
  getDoc,
  getDocs,
  increment,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

// "Me Interesa", guardados y preguntas de autos y propiedades: el mismo
// esquema que ofertas/eventos (ver homeUtils.js), pero con un "type" para
// saber en qué colección vive cada publicación. Las preguntas van aparte de
// questions/answers (que son de ofertas) en listingQuestions/listingAnswers.
export const LISTING_TYPES = {
  car: { collection: "cars", saved: "savedCars", path: "/cars" },
  place: { collection: "places", saved: "savedPlaces", path: "/trips" },
};

export const listingUrl = (type, id) =>
  `${window.location.origin}${LISTING_TYPES[type].path}/${id}`;

// Igual que handleMeSirve: transacción para que un doble click no sume de a 2.
// El like queda en el array "likes" de la publicación; como los feeds de autos
// y propiedades usan onSnapshot, el contador se actualiza solo.
export const toggleListingLike = async (type, listingId, userId) => {
  if (!userId || !listingId) return;
  const ref = doc(db, LISTING_TYPES[type].collection, listingId);
  try {
    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(ref);
      const liked = (snap.data()?.likes || []).includes(userId);
      transaction.update(ref, {
        likes: liked ? arrayRemove(userId) : arrayUnion(userId),
        helpedCount: increment(liked ? -1 : 1),
      });
    });
  } catch (error) {
    console.error("Error al actualizar 'Me Interesa':", error);
  }
};

// Guardados: un doc por usuario { [listingId]: true }, como savedOffers.
export const toggleSaveListing = async (type, userId, listingId, isSaved) => {
  if (!userId || !listingId) return false;
  try {
    const ref = doc(db, LISTING_TYPES[type].saved, userId);
    if (isSaved) {
      await updateDoc(ref, { [listingId]: deleteField() });
    } else {
      await setDoc(ref, { [listingId]: true }, { merge: true });
    }
    return true;
  } catch (error) {
    console.error("Error al guardar/quitar la publicación:", error);
    return false;
  }
};

export const fetchSavedListingIds = async (type, userId) => {
  if (!userId) return [];
  try {
    const snap = await getDoc(doc(db, LISTING_TYPES[type].saved, userId));
    return snap.exists() ? Object.keys(snap.data()) : [];
  } catch (error) {
    console.error("Error al obtener los guardados:", error);
    return [];
  }
};

// "No me interesa": hiddenListings/{uid} = { "<type>_<id>": true }, un solo doc
// para autos y propiedades (como hiddenOffers, pero con el tipo en la clave).
export const hideListing = async (type, userId, listingId) => {
  if (!userId || !listingId) return;
  try {
    await setDoc(doc(db, "hiddenListings", userId), { [`${type}_${listingId}`]: true }, { merge: true });
  } catch (error) {
    console.error("Error al ocultar la publicación:", error);
  }
};

export const fetchHiddenListingIds = async (type, userId) => {
  if (!userId) return [];
  try {
    const snap = await getDoc(doc(db, "hiddenListings", userId));
    const prefix = `${type}_`;
    return snap.exists()
      ? Object.keys(snap.data()).filter((k) => k.startsWith(prefix)).map((k) => k.slice(prefix.length))
      : [];
  } catch (error) {
    console.error("Error al obtener las publicaciones ocultas:", error);
    return [];
  }
};

export const fetchListingsByIds = async (type, ids) => {
  const uniqueIds = [...new Set(ids)].filter(Boolean);
  const results = [];
  for (let i = 0; i < uniqueIds.length; i += 30) {
    const chunk = uniqueIds.slice(i, i + 30);
    try {
      const snap = await getDocs(
        query(collection(db, LISTING_TYPES[type].collection), where(documentId(), "in", chunk)),
      );
      snap.forEach((docSnap) => results.push({ id: docSnap.id, ...docSnap.data() }));
    } catch (error) {
      console.error("Error al obtener publicaciones guardadas:", error);
    }
  }
  return results;
};

// Nombre y foto de quien pregunta/responde, cacheados por sesión.
const userCache = {};
const fetchUserInfo = (uid) => {
  if (!userCache[uid]) {
    userCache[uid] = getDoc(doc(db, "users", uid))
      .then((snap) => ({
        displayName: snap.data()?.displayName || "",
        profileImageUrl: snap.data()?.profileImageUrl || "",
      }))
      .catch(() => ({ displayName: "", profileImageUrl: "" }));
  }
  return userCache[uid];
};

const byCreatedAt = (a, b) =>
  (a.createdAt?.toMillis?.() ?? Infinity) - (b.createdAt?.toMillis?.() ?? Infinity);

// Preguntas con sus respuestas, en vivo. Solo filtros de igualdad y orden del
// lado del cliente, así no hace falta un índice compuesto.
export const subscribeListingQuestions = (type, listingId, callback) => {
  let questions = [];
  let answers = [];
  let version = 0;

  const emit = async () => {
    const current = ++version;
    const uids = [...new Set([...questions, ...answers].map((d) => d.userId).filter(Boolean))];
    const infos = Object.fromEntries(
      await Promise.all(uids.map(async (uid) => [uid, await fetchUserInfo(uid)])),
    );
    if (current !== version) return;
    callback(
      [...questions].sort(byCreatedAt).map((q) => ({
        ...q,
        ...infos[q.userId],
        answers: answers
          .filter((a) => a.questionId === q.id)
          .sort(byCreatedAt)
          .map((a) => ({ ...a, ...infos[a.userId] })),
      })),
    );
  };

  // Sin permiso (p.ej. reglas todavía sin desplegar) queda sin preguntas en
  // vez de tirar un error sin atrapar por cada publicación.
  const onError = (error) => console.error("Error al obtener las preguntas:", error);
  const filters = [where("listingType", "==", type), where("listingId", "==", listingId)];
  const unsubQuestions = onSnapshot(query(collection(db, "listingQuestions"), ...filters), (snap) => {
    questions = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    emit();
  }, onError);
  const unsubAnswers = onSnapshot(query(collection(db, "listingAnswers"), ...filters), (snap) => {
    answers = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    emit();
  }, onError);
  return () => {
    unsubQuestions();
    unsubAnswers();
  };
};

export const postListingQuestion = async (type, listingId, userId, question) => {
  try {
    await addDoc(collection(db, "listingQuestions"), {
      listingType: type,
      listingId,
      userId,
      question,
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error al publicar la pregunta:", error);
  }
};

export const postListingAnswer = async (type, listingId, questionId, userId, answer) => {
  try {
    await addDoc(collection(db, "listingAnswers"), {
      listingType: type,
      listingId,
      questionId,
      userId,
      answer,
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error al publicar la respuesta:", error);
  }
};
